import type { ApiAttachment } from '../api/types';

export interface ScheduledMessage {
  id: string;
  chatId: string;
  text: string;
  scheduledAt: number;
  createdAt: number;
  attachments?: ApiAttachment[];
}

const STORAGE_KEY = 'scheduled_messages';

// Add event emitter for scheduled message changes
const SCHEDULED_MESSAGE_EVENTS = {
  ADDED: 'scheduledMessageAdded',
  REMOVED: 'scheduledMessageRemoved',
} as const;

const eventTarget = new EventTarget();

export function onScheduledMessageAdded(callback: (message: ScheduledMessage) => void) {
  eventTarget.addEventListener(SCHEDULED_MESSAGE_EVENTS.ADDED, ((e: CustomEvent) => callback(e.detail)) as EventListener);
}

export function onScheduledMessageRemoved(callback: (messageId: string) => void) {
  eventTarget.addEventListener(SCHEDULED_MESSAGE_EVENTS.REMOVED, ((e: CustomEvent) => callback(e.detail)) as EventListener);
}

export function saveScheduledMessage(message: Omit<ScheduledMessage, 'id' | 'createdAt'>) {
  const messages = getAllScheduledMessages();
  const newMessage: ScheduledMessage = {
    ...message,
    id: Math.random().toString(36).substring(2),
    createdAt: Date.now(),
  };
  
  messages.push(newMessage);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  
  // Notify listeners
  eventTarget.dispatchEvent(new CustomEvent(SCHEDULED_MESSAGE_EVENTS.ADDED, { detail: newMessage }));
  
  return newMessage;
}

export function getAllScheduledMessages(): ScheduledMessage[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
}

export function getScheduledMessagesByChat(chatId: string): ScheduledMessage[] {
  const messages = getAllScheduledMessages();
  return messages.filter(msg => msg.chatId === chatId);
}

export function deleteScheduledMessage(messageId: string) {
  const messages = getAllScheduledMessages();
  const filtered = messages.filter(msg => msg.id !== messageId);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  
  // Notify listeners
  eventTarget.dispatchEvent(new CustomEvent(SCHEDULED_MESSAGE_EVENTS.REMOVED, { detail: messageId }));
}

export function updateScheduledMessage(messageId: string, updates: Partial<ScheduledMessage>) {
  const messages = getAllScheduledMessages();
  const index = messages.findIndex(msg => msg.id === messageId);
  if (index === -1) return;
  
  messages[index] = { ...messages[index], ...updates };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
} 